'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiHooks, type Idea } from '@/lib/api';

const DESKTOP_COLUMN_COUNT = 5;
const TABLET_COLUMN_COUNT = 3;
const MOBILE_COLUMN_COUNT = 2;
const DEFAULT_MAX_IDEAS = 15;

export function IdeasMasonry({
  max = DEFAULT_MAX_IDEAS,
  onSelect,
}: {
  max?: number;
  /** When provided, cards become buttons that fire onSelect. Without
   * it (e.g. on the public landing) cards stay as plain divs — the
   * landing's CTA is "sign in", not "click an idea". */
  onSelect?: (idea: Idea) => void;
} = {}) {
  const { data: ideas, isLoading } = useQuery({
    queryKey: ['ideas'],
    queryFn: apiHooks.getIdeas,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <IdeasSkeleton />;
  if (!ideas || ideas.length === 0) return null;

  const limited = ideas.slice(0, max);

  return (
    <>
      <div className="hidden md:block">
        <MasonryGrid ideas={limited} columnCount={DESKTOP_COLUMN_COUNT} onSelect={onSelect} />
      </div>
      <div className="hidden sm:block md:hidden">
        <MasonryGrid ideas={limited} columnCount={TABLET_COLUMN_COUNT} onSelect={onSelect} />
      </div>
      <div className="sm:hidden">
        <MasonryGrid ideas={limited} columnCount={MOBILE_COLUMN_COUNT} onSelect={onSelect} />
      </div>
    </>
  );
}

function MasonryGrid({
  ideas,
  columnCount,
  onSelect,
}: {
  ideas: Idea[];
  columnCount: number;
  onSelect?: (idea: Idea) => void;
}) {
  const columns = balanceMasonryColumns(ideas, columnCount);
  return (
    <div className="flex gap-2">
      {columns.map((col, i) => (
        <div key={i} className="flex flex-1 flex-col gap-2">
          {col.map((idea) => (
            <IdeaCard key={idea.id} idea={idea} onSelect={onSelect} />
          ))}
        </div>
      ))}
    </div>
  );
}

function IdeaCard({
  idea,
  onSelect,
}: {
  idea: Idea;
  onSelect?: (idea: Idea) => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const aspect =
    idea.imageWidth && idea.imageHeight
      ? { aspectRatio: `${idea.imageWidth}/${idea.imageHeight}` }
      : { minHeight: 160 };

  const inner = (
    <>
      {!loaded && <div className="absolute inset-0 animate-pulse bg-white/5" />}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={idea.imageUrl}
        alt={idea.prompt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={`block h-auto w-full transition-opacity duration-500 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </>
  );

  if (onSelect) {
    return (
      <button
        type="button"
        onClick={() => onSelect(idea)}
        className="group relative block w-full overflow-hidden rounded-[12px] bg-[#1a1a1d] text-left"
        style={aspect}
      >
        {inner}
      </button>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-[12px] bg-[#1a1a1d]"
      style={aspect}
    >
      {inner}
    </div>
  );
}

function IdeasSkeleton() {
  const heights = [260, 200, 280, 220, 240];
  return (
    <div className="flex gap-2">
      {Array.from({ length: DESKTOP_COLUMN_COUNT }).map((_, ci) => (
        <div key={ci} className="flex flex-1 flex-col gap-2">
          {heights.map((h, ti) => (
            <div
              key={ti}
              className="animate-pulse rounded-[12px] bg-white/5"
              style={{ height: h }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function balanceMasonryColumns(items: Idea[], columnCount: number): Idea[][] {
  const columns: Idea[][] = Array.from({ length: columnCount }, () => []);
  const heights: number[] = new Array(columnCount).fill(0);

  for (const item of items) {
    const ratio =
      item.imageWidth && item.imageHeight ? item.imageHeight / item.imageWidth : 1;
    let minIdx = 0;
    for (let i = 1; i < columnCount; i++) {
      if (heights[i]! < heights[minIdx]!) minIdx = i;
    }
    columns[minIdx]!.push(item);
    heights[minIdx]! += ratio;
  }

  return columns;
}
